import React, { useState } from 'react';
import { RotateCcw } from 'react-feather';

import ContactDetailModal from '@/shared/components/Common/ContactDetailModal';
import Paging from '@/shared/components/Common/Paging';
import SearchBar from '@/shared/components/Common/SearchBar';
import { useInitialPage } from '@/shared/hooks/useInitialPage';
import { useContactList } from './hooks/useContactList';

import ContactListTable from './components/UI/ContactListTable';
import { ContactFilter, FilterOption } from './types/contactTypes';

const ContactListPage: React.FC = () => {
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null,
  );

  useInitialPage();
  const {
    contacts,
    currentPage,
    totalPages,
    keyword,
    filter,
    isLoading,
    hasError,
    isEmpty,
    shouldReset,
    totalCount,
    handlePageChange,
    handleSearch,
    handleFilterChange,
    handleReset,
    handleResetComplete,
    handleRefresh,
    refreshContactList,
    error,
  } = useContactList();

  const filterOptions: FilterOption[] = [
    { value: 'ALL', label: 'Contact Status' },
    { value: 'UNREAD', label: 'Unread' },
    { value: 'READ', label: 'Read' },
  ];

  const handleFilterSelectChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedValue = event.target.value as ContactFilter;
    handleFilterChange(selectedValue);
  };

  const handleContactRowClick = (contactId: number) => {
    setSelectedContactId(contactId);
  };

  const handleContactModalClose = () => {
    setSelectedContactId(null);
    // 모달이 닫힐 때 Contact 목록 새로고침
    refreshContactList();
  };

  return (
    <div className="min-h-screen w-full bg-[#F1F1F1]">
      <div className="px-8 pt-6">
        <h2 className="text-2xl font-bold text-[#1D1D1D]">Contact</h2>

        <div className="mb-6 mt-8 flex items-center justify-end">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded bg-[#E2E2E2] transition-colors hover:bg-opacity-80"
              onClick={handleReset}
              disabled={isLoading}
            >
              <RotateCcw size={16} className="text-[#1D1D1D]" />
            </button>
            <select
              name="answer-status"
              id="answer-status"
              className="h-7 cursor-pointer rounded border-none bg-[#E2E2E2] px-2 text-sm outline-none"
              value={filter}
              onChange={handleFilterSelectChange}
              disabled={isLoading}
            >
              {filterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <form className="mb-0 h-10 w-full bg-white" action="#" method="get">
          <SearchBar
            onSearch={handleSearch}
            shouldReset={shouldReset}
            onResetComplete={handleResetComplete}
            initialValue={keyword}
          />
        </form>

        <div className="w-full overflow-auto border border-t-0 border-[#DEDEDE] bg-[#F1F1F1]">
          <ContactListTable
            contacts={contacts}
            isLoading={isLoading}
            hasError={hasError}
            isEmpty={isEmpty}
            error={error}
            onRowClick={handleContactRowClick}
          />
        </div>

        {!isLoading && !hasError && totalPages > 0 && (
          <div className="flex-none border border-x-[#DEDEDE] bg-[#E8E8E8] pb-4 pt-3">
            <Paging
              currentPage={currentPage}
              setCurrentPage={handlePageChange}
              totalPage={totalPages}
            />
          </div>
        )}
      </div>

      {/* Contact Detail Modal */}
      {selectedContactId && (
        <ContactDetailModal
          contactId={selectedContactId}
          onClose={handleContactModalClose}
          userUuid={undefined}
        />
      )}
    </div>
  );
};

export default ContactListPage;
